import React from 'react';
import * as THREE from 'three';
import { invalidate } from '@react-three/fiber';
import { TransformControls } from '@react-three/drei';
import { Model } from '../../hooks/useModelState';
import {
  cadToThreeJsPositionWithOffset,
  threeJsPositionToCad
} from '../../Scenes/scene-utils';
import * as PCConstants from '../PCConstants';
import { BULK_IMPORT_GHOST_NODE_ID } from '../../Scenes/BulkImportSolidsScene/bulkImportGhost';
import {
  getBulkImportSolidsSceneState,
  getCurrentSolid,
  updateBulkImportSolidsSceneState
} from '../../Scenes/BulkImportSolidsScene/bulkImportSolidsSceneState';

interface BulkImportDraggableNodeProps {
  model: Model;
  offset: THREE.Vector3;
  scale: number;
}

const BulkImportDraggableNode = ({ model, offset, scale }: BulkImportDraggableNodeProps) => {
  const groupRef = React.useRef<THREE.Group | null>(null);
  const [groupNode, setGroupNode] = React.useState<THREE.Group | null>(null);
  const modelRef = React.useRef(model);
  modelRef.current = model;

  const ghost = model.nodes[BULK_IMPORT_GHOST_NODE_ID];

  const worldPosition = React.useMemo(() => {
    if (!ghost) return null;
    const [x, y, z] = cadToThreeJsPositionWithOffset(ghost.coords, offset, scale);
    return new THREE.Vector3(x, y, z);
  }, [ghost, offset, scale]);

  const handleObjectChange = React.useCallback(() => {
    if (!groupRef.current || scale === 0) return;
    const cad = threeJsPositionToCad(groupRef.current.position, offset, scale);
    const cadPos = { x: cad.x, y: cad.y, z: cad.z };

    modelRef.current.setNodeProperties(BULK_IMPORT_GHOST_NODE_ID, {
      coords: cadPos as any,
    });

    const sceneState = getBulkImportSolidsSceneState();
    const solid = getCurrentSolid();
    const existingDecision = solid?.guid ? sceneState.decisions[solid.guid] : undefined;
    const updates: Partial<typeof sceneState> = { projectedNodePosition: cadPos };
    if (existingDecision && solid?.guid) {
      updates.decisions = {
        ...sceneState.decisions,
        [solid.guid]: { ...existingDecision, newNodePosition: cadPos },
      };
    }
    updateBulkImportSolidsSceneState(updates);

    invalidate();
  }, [offset, scale]);

  if (!ghost || !worldPosition) return null;

  return (
    <>
      <group
        ref={(node) => {
          groupRef.current = node;
          setGroupNode(node);
        }}
        position={worldPosition}
      >
        <mesh>
          <sphereGeometry args={[3 * scale, 16, 16]} />
          <meshBasicMaterial color={PCConstants.STOPE_COLOUR} />
        </mesh>
      </group>

      {groupNode && (
        <TransformControls
          object={groupNode}
          mode="translate"
          showX
          showY
          showZ
          onObjectChange={handleObjectChange}
        />
      )}
    </>
  );
};

export default BulkImportDraggableNode;
